<template>
  <div class="tool-page">
    <!-- 面包屑导航 -->
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('json.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('json.title') }}</h1>
      <p>{{ $t('json.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="actions">
        <button class="btn primary" @click="formatJson">{{ $t('json.format') }}</button>
        <button class="btn" @click="compressJson">{{ $t('json.compress') }}</button>
        <button class="btn" @click="copyResult" :disabled="!output">{{ $t('common.copyResult') }}</button>
        <button class="btn" @click="clearAll">{{ $t('common.clear') }}</button>
      </div>

      <div v-if="errorMessage" class="alert error">{{ errorMessage }}</div>
      <div v-if="successMessage" class="alert success">{{ successMessage }}</div>

      <div class="editor-container">
        <div class="editor-panel">
          <label>{{ $t('json.inputLabel') }}</label>
          <textarea
            v-model="input"
            :placeholder="$t('json.inputPlaceholder')"
            spellcheck="false"
          ></textarea>
        </div>

        <div class="editor-panel">
          <label>{{ $t('json.outputLabel') }}</label>
          <textarea
            v-model="output"
            readonly
            :placeholder="$t('json.outputPlaceholder')"
            spellcheck="false"
          ></textarea>
        </div>
      </div>

      <div class="options">
        <label class="option-label">
          {{ $t('json.indentSpaces') }}:
          <select v-model="indentSize">
            <option :value="2">2</option>
            <option :value="4">4</option>
            <option :value="8">8</option>
          </select>
        </label>
      </div>
    </div>

    <!-- 工具说明 -->
    <div class="tool-description">
      <h2>{{ $t('json.whatIsJson') }}</h2>
      <p>{{ $t('json.whatIsJsonDesc') }}</p>
      
      <h2>{{ $t('json.features') }}</h2>
      <ul>
        <li><strong>{{ $t('json.featureFormat') }}</strong></li>
        <li><strong>{{ $t('json.featureCompress') }}</strong></li>
        <li><strong>{{ $t('json.featureValidate') }}</strong></li>
        <li><strong>{{ $t('json.featureCopy') }}</strong></li>
      </ul>

      <h2>{{ $t('json.howToUse') }}</h2>
      <ol>
        <li v-for="(step, index) in $t('json.howToUseSteps')" :key="index">{{ step }}</li>
      </ol>

      <h2>{{ $t('json.example') }}</h2>
      <p>{{ $t('json.exampleDesc') }}</p>
      <pre class="code-example">{
  "name": "John",
  "age": 25,
  "email": "john@example.com",
  "hobbies": ["reading", "coding", "travel"],
  "address": {
    "city": "Beijing",
    "district": "Haidian"
  }
}</pre>

      <h2>{{ $t('json.commonErrors') }}</h2>
      <ul>
        <li><strong>{{ $t('json.errorQuotes') }}</strong></li>
        <li><strong>{{ $t('json.errorTrailingComma') }}</strong></li>
        <li><strong>{{ $t('json.errorSingleQuote') }}</strong></li>
        <li><strong>{{ $t('json.errorComments') }}</strong></li>
      </ul>

      <h2>{{ $t('json.jsonHistory') }}</h2>
      <p>{{ $t('json.jsonHistoryDesc') }}</p>

      <h2>{{ $t('json.jsonVsXml') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('json.jsonVsXmlItems')" :key="'xml'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('json.jsonDataTypes') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('json.jsonDataTypesItems')" :key="'type'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('json.jsonUseCases') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('json.jsonUseCasesItems')" :key="'usecase'+index">{{ item }}</li>
      </ul>

      <h2>{{ $t('json.jsonBestPractices') }}</h2>
      <ul>
        <li v-for="(item, index) in $t('json.jsonBestPracticesItems')" :key="'bp'+index">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JsonFormatter',
  data() {
    return {
      input: '',
      output: '',
      errorMessage: '',
      successMessage: '',
      indentSize: 2
    }
  },
  methods: {
    formatJson() {
      this.errorMessage = ''
      this.successMessage = ''
      
      if (!this.input.trim()) {
        this.errorMessage = this.$t('json.emptyError')
        return
      }

      try {
        const parsed = JSON.parse(this.input)
        this.output = JSON.stringify(parsed, null, this.indentSize)
        this.successMessage = this.$t('json.formatSuccess')
      } catch (e) {
        this.errorMessage = this.$t('json.formatError') + ': ' + e.message
        this.output = ''
      }
    },

    compressJson() {
      this.errorMessage = ''
      this.successMessage = ''
      
      if (!this.input.trim()) {
        this.errorMessage = this.$t('json.emptyError')
        return
      }

      try {
        const parsed = JSON.parse(this.input)
        this.output = JSON.stringify(parsed)
        this.successMessage = this.$t('json.compressSuccess')
      } catch (e) {
        this.errorMessage = this.$t('json.formatError') + ': ' + e.message
        this.output = ''
      }
    },

    async copyResult() {
      if (!this.output) return
      
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(this.output)
        } else {
          this.fallbackCopy(this.output)
          return
        }
        this.successMessage = this.$t('common.copied')
      } catch (e) {
        this.fallbackCopy(this.output)
      }
    },

    fallbackCopy(text) {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        this.successMessage = this.$t('common.copied')
      } catch (e) {
        this.errorMessage = this.$t('common.copyFailed')
      }
      document.body.removeChild(textArea)
    },

    clearAll() {
      this.input = ''
      this.output = ''
      this.errorMessage = ''
      this.successMessage = ''
    }
  }
}
</script>

<style scoped>
.tool-page {
  max-width: 1200px;
  margin: 0 auto;
}

/* 面包屑导航 */
.breadcrumb {
  margin-bottom: 24px;
  font-size: 14px;
  color: var(--text-secondary);
}

.breadcrumb a {
  color: var(--accent-color);
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

.breadcrumb .separator {
  margin: 0 8px;
  color: var(--text-muted);
}

.breadcrumb .current {
  color: var(--text-secondary);
}

.tool-header {
  text-align: center;
  margin-bottom: 32px;
}

.tool-header h1 {
  font-size: 32px;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.tool-header p {
  color: var(--text-secondary);
  font-size: 16px;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.tool-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 24px;
  box-shadow: var(--shadow-md);
  transition: background 0.3s, border-color 0.3s;
}

.actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.btn {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn:hover {
  background: var(--bg-hover);
}

.btn.primary {
  background: var(--accent-color);
  border-color: var(--accent-color);
  color: white;
}

.btn.primary:hover {
  background: var(--accent-hover);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.alert {
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.alert.error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: var(--error-color);
}

.alert.success {
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: var(--success-color);
}

.editor-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

@media (max-width: 768px) {
  .editor-container {
    grid-template-columns: 1fr;
  }
}

.editor-panel {
  display: flex;
  flex-direction: column;
}

.editor-panel label {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.editor-panel textarea {
  min-height: 400px;
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  color: var(--text-primary);
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  transition: background 0.3s, border-color 0.3s, color 0.3s;
  width: 100%;
  box-sizing: border-box;
}

.editor-panel textarea:focus {
  outline: none;
  border-color: var(--accent-color);
}

.editor-panel textarea::placeholder {
  color: var(--text-muted);
}

.options {
  margin-top: 16px;
  display: flex;
  gap: 20px;
  align-items: center;
}

.option-label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 14px;
}

.option-label select {
  background: var(--bg-input);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 6px 12px;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.3s, border-color 0.3s, color 0.3s;
}

/* 工具说明 */
.tool-description {
  margin-top: 32px;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  padding: 32px;
  box-shadow: var(--shadow-sm);
}

.tool-description h2 {
  font-size: 20px;
  color: var(--text-primary);
  margin: 24px 0 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid var(--accent-light);
}

.tool-description h2:first-child {
  margin-top: 0;
}

.tool-description p {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 16px;
}

.tool-description ul,
.tool-description ol {
  padding-left: 24px;
  margin-bottom: 16px;
}

.tool-description li {
  font-size: 15px;
  color: var(--text-secondary);
  line-height: 1.8;
  margin-bottom: 8px;
}

.tool-description strong {
  color: var(--text-primary);
}

.code-example {
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 16px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 13px;
  line-height: 1.6;
  overflow-x: auto;
  color: var(--text-primary);
}
</style>
